import { BaseElements, BaseObject, BaseService, BaseInjector } from '../../js/base';
import { querySelector, KeyCodes, querySelectorAll } from '../../utility/utils';

class Texts extends BaseObject {}

class ClassList extends BaseObject {
    
}

class Selectors extends BaseObject {
    exportCitationTriggers = '[data-target="#exportCitation"]';
    exportCitationSelect = '#citation-format';
    cslResponse = '.csl-response';
    checkboxInputs = '.card-checkbox:checked';
    warningMessage = '#export-warning';
    cslWrapper = '.csl-wrapper';
    downloadBtn = '.download__btn';
    copyBtn = '.copy__btn';
    dialogCloseBtn = ".dialog-close";
    localCitationBtn = ".source_clarify_content #exportCitationBtnTest";
}

class Elements extends BaseElements<Selectors> {
    exportCitationTriggers: NodeListOf<HTMLAnchorElement>;
    exportCitationSelect: HTMLSelectElement;
    cslResponse: HTMLElement;
    checkboxInputs: NodeListOf<HTMLInputElement>;
    warningMessage: HTMLElement;
    cslWrapper: HTMLElement;
    downloadBtn: HTMLAnchorElement;
    copyBtn: HTMLAnchorElement;
    dialogCloseBtn: HTMLButtonElement;
    localCitationBtn: HTMLAnchorElement;
    
    constructor(selectors: Selectors) {
        super(selectors, false);
    }

    setWrapper(wrapper: HTMLElement) {
        this.wrapper = wrapper;
        this.initElements()
    }

    // Loop over all elements and init them
    initElements() {
        this.initElementsByLoop(this.wrapper);
        this.exportCitationTriggers = querySelectorAll(this.selectors.exportCitationTriggers);
        this.checkboxInputs = querySelectorAll(this.selectors.checkboxInputs);
        this.localCitationBtn = querySelector(this.selectors.localCitationBtn);
    }
}

interface ExportCitation extends BaseService<Selectors, Elements, ClassList, Texts> { }

@BaseInjector(Selectors, Elements, ClassList, Texts)
class ExportCitation {
    responseFormat: {};
    dois: string = null;
    selectCiteStyle: string = null;
    loadingAdditionalInfo: string = '';
    response: any = null;
    citationLimited: boolean = false;
    citationLimitNumber: number = 0;
    dataToCopy: string = '';


    constructor(protected readonly wrapper: any) {}

    initialize(): void {
        this.elements.setWrapper(this.wrapper);
        this.addEventListeners();      
    }

    addEventListeners(): void {
        this.elements.exportCitationTriggers.forEach( (elem) => {
            elem.addEventListener('click' , () => {
                // for single citation btn
                if(!elem.id) {
                    this.wrapper.showModal();
                }
                this.resetPopup(elem);
                this.dois = this.collectDois(elem).toString();
                this.loadCiteProc(this.elements.exportCitationSelect);
            })
        })

        this.elements.exportCitationSelect.addEventListener('change' ,  () => {
            this.loadCiteProc(this.elements.exportCitationSelect)
        })

        this.elements.copyBtn.addEventListener('click' , () => {
            let cp = navigator.clipboard;
            let copyButton = this.elements.copyBtn;
            let textCP = this.dataToCopy;
            cp.writeText(textCP).then(()=>{
                copyButton.classList.add("clicked")
                setTimeout(()=>{
                    copyButton.classList.remove("clicked")
                }
                , 500);
            });
        })

        // just for demo testing locally 
        if (this.elements.localCitationBtn) {
            this.elements.localCitationBtn.addEventListener('click' , ()=> {
                this.wrapper.showModal();
            })
        }

        // this code will be removed when dialog ticket merged
        this.elements.dialogCloseBtn?.addEventListener('click' , ()=> {
            this.wrapper.close();
        })
    }

    resetPopup(elem : HTMLAnchorElement): void { 
        this.elements.cslResponse.innerHTML  = '';
        this.responseFormat = {};
        this.elements.exportCitationSelect.selectedIndex = 0;
        const modalId = elem.getAttribute('data-target');
        const $modal = document.querySelector(modalId);
        $modal.querySelectorAll('.copy__btn, .download__btn').forEach(element => {
            element.classList.add('disabled');
        });
        this.elements.exportCitationSelect.disabled  = true;
    }
    
    collectDois(elem : HTMLAnchorElement ): Array<string>{
        let doisArray = [];
        let multiDois = false;

        if (elem.classList.contains('export-citation')) {
            multiDois = true;
        }

        if (multiDois) {
            this.elements.checkboxInputs = querySelectorAll(this.selectors.checkboxInputs);
            this.elements.checkboxInputs.forEach(function (checkbox) {
                const $this = checkbox;
                const doiValue = $this.getAttribute('name') === 'doi' ? $this.value : $this.getAttribute('name');
                doisArray.push(doiValue);
            });
        } else {
            elem.querySelectorAll('[name="doiVal"]').forEach(function (input : HTMLInputElement) {
                doisArray.push(input.value);
            });
        }

        return doisArray;
    }

    loadCiteProc(elem : HTMLSelectElement ): void {
        this.selectCiteStyle = elem.value;
        const selectedFormat  = ( document.querySelector('[value="' + this.selectCiteStyle + '"]') as HTMLElement ).dataset.format;
        const targetCustomCSLFIle = 'custom-' + this.selectCiteStyle;
        this.elements.warningMessage.innerHTML = '';

        if (this.selectCiteStyle) {
            this.elements.cslWrapper.querySelectorAll('.copy__btn, .download__btn').forEach(function (element : HTMLAnchorElement) {
                element.classList.add('disabled');
            });

            this.elements.exportCitationSelect.disabled = true;

            const loadingText = 'Loading ' + this.loadingAdditionalInfo + '... ';
            this.elements.cslResponse.innerHTML = '<span>' + loadingText + '</span>';

            const action_server = '/action/exportCiteProcCitation';
            // const action_server = '/specs/libraries/ui-theme-eureka/widgets/export-citations/dummy-csl-metaData-one-citation.json'; // dummy for UX3 testing
            // const action_server = '/specs/libraries/ui-theme-eureka/widgets/export-citations/dummy-csl-metaData-multi-citation.json'; // dummy for UX3 testing
            const params = this.setParams(targetCustomCSLFIle, selectedFormat);

            if (typeof this.responseFormat[this.selectCiteStyle] !== 'undefined') {
                this.setContent(this.responseFormat[this.selectCiteStyle].content);
                this.downloadCiteProc();
            } else {
                this.performServerSideAjax(action_server, params, this.selectCiteStyle);
            }

            (this.elements.cslWrapper.querySelector('[name="dois"]') as HTMLInputElement ).value = this.dois;
            (this.elements.cslWrapper.querySelector('[name="format"]') as HTMLInputElement ).value = selectedFormat;
        }   
    }
    
    setParams (targetCustomCSLFIle: string, selectedFormat: string): Object {
        return {dois: this.dois, targetFile: targetCustomCSLFIle, format: selectedFormat};
    }

    setContent(content : string): void {
        this.elements.cslResponse.innerHTML = content;
        (this.elements.cslWrapper.querySelector('[name="content"]') as HTMLInputElement ).value = this.elements.cslResponse.textContent;
        this.elements.cslWrapper.querySelectorAll('.copy__btn, .download__btn').forEach(function (element) {
            element.classList.remove('disabled');
        });
        this.elements.exportCitationSelect.removeAttribute('disabled');

        if (this.responseFormat[this.selectCiteStyle].warning) {
            this.elements.warningMessage.innerHTML = this.getWarningMessage(this.responseFormat[this.selectCiteStyle].warning);
        }
    }

    getWarningMessage(message : string ): string {
        return message;
    }

    async performServerSideAjax(url: string, params : any, selectCiteStyle: string) {
        const queryString = new URLSearchParams(params).toString();
        const requestUrl = `${url}?${queryString}`;
      
        try {
          const response = await fetch(requestUrl);
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          const responseData = await response.json();
          this.response = responseData;
          this.responseFormat[selectCiteStyle] = this.response;
          this.setContent(this.responseFormat[selectCiteStyle].content);
          this.downloadCiteProc();
        } catch (error) {
          console.error('There has been a problem with your fetch operation:', error);
        }
    }

    downloadCiteProc(): void {
        let $el = this.elements.downloadBtn;
        let downloadContainer = this.elements.cslResponse as any;
        let downloadData = '';
      
        if (downloadContainer.classList.contains('csl-response')) {

            if ((this.elements.cslResponse.querySelectorAll('.csl-right-inline')).length) {
                downloadContainer = this.elements.cslResponse.querySelectorAll('.csl-right-inline');

            } else if (this.elements.cslResponse.querySelectorAll('.csl-entry').length) {
                downloadContainer = this.elements.cslResponse.querySelectorAll('.csl-entry'); 
            }

            if(downloadContainer.length == 1) {
                downloadData = downloadContainer[0].innerHTML;

                if (/<\/?[a-z][\s\S]*>/i.test(downloadContainer[0].innerHTML)) {
                    downloadData = downloadContainer[0].textContent;
                }
            }else {
                let downloadMultiText = '';
                Array.from(downloadContainer).forEach(function(element: HTMLElement) {
                    let $this = element;
                    if (/<\/?[a-z][\s\S]*>/i.test($this.innerHTML)) {
                        downloadMultiText += $this.textContent + '\n\n';
                    } else {
                        downloadMultiText += $this.innerHTML + '\n\n';
                    }
                });
                downloadData = downloadMultiText;
            }
        }
      
        downloadData = this.additionalDownloadDataFormat(downloadData);
        this.dataToCopy = downloadData;
      
        $el.setAttribute(
          'href',
          'data:' +
            this.responseFormat[this.selectCiteStyle].contentType +
            ';charset=utf-8,' +
            encodeURIComponent(downloadData)
        );
        $el.setAttribute(
          'download',
          this.responseFormat[this.selectCiteStyle].fileName +
            '.' +
            this.responseFormat[this.selectCiteStyle].suffix
        );
    }

    additionalDownloadDataFormat(downloadData : string): string {
        return downloadData;
    }
}


export default ExportCitation;

export {
    Elements as ExportCitationElements,
    Selectors as ExportCitationSelectors,
    ClassList as ExportCitationClassList,
    Texts as ExportCitationTexts
}